// ============================================================================
// Supabase client singleton.
//
// Auth model: Firebase Auth (Google SSO) is the identity provider. The
// `firebase-jwt-bridge` Edge Function exchanges a Firebase ID token for a
// Supabase-signed JWT carrying the user's UUID + role. That token is passed
// here via setSupabaseToken() and injected on every request through a
// custom fetch wrapper, so RLS sees the user as `authenticated` with
// `auth.uid() = user_profiles.id`.
//
// Why mutate via a module-level token rather than recreate the client:
//   * Single SupabaseClient instance keeps query state, channel subs, etc.
//   * Components import `supabase` directly without an accessor pattern.
//   * Token refresh (every ~55min) is transparent — no React re-renders.
// ============================================================================

import { createClient, SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'Missing Supabase env vars. Copy .env.example to .env.local and fill in ' +
      'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
  );
}

// ----- Token state ----------------------------------------------------------
// `currentSupabaseToken` is the Supabase JWT minted by the bridge. It's set
// by AuthProvider after a successful Firebase sign-in + bridge exchange, and
// cleared on sign-out. While null, the client falls back to anon (RLS will
// block anything that requires auth.uid()).

let currentSupabaseToken: string | null = null;

export function setSupabaseToken(token: string | null): void {
  currentSupabaseToken = token;
}

export function getSupabaseToken(): string | null {
  return currentSupabaseToken;
}

// ----- Reactive 401 recovery -------------------------------------------------
// The proactive refresh timer in AuthProvider can die silently: window
// setTimeout doesn't fire across laptop sleep, and background tabs are
// throttled. When that happens the bridged JWT expires while cached reads
// keep rendering — and the next WRITE goes out with a dead token and fails
// (AFB-0029: every cutover flip showed "Cutover failed."). Fix: when any
// request comes back 401 while we hold a bridged token, ask AuthProvider to
// re-run the bridge once (single-flight across concurrent 401s) and retry
// the request one time with the fresh token.

let refreshHandler: (() => Promise<boolean>) | null = null;
let refreshInFlight: Promise<boolean> | null = null;

/** Registered by AuthProvider; re-runs the Firebase→Supabase bridge. */
export function setAuthRefreshHandler(
  fn: (() => Promise<boolean>) | null,
): void {
  refreshHandler = fn;
}

function tryRefreshOnce(): Promise<boolean> {
  if (!refreshHandler) return Promise.resolve(false);
  if (!refreshInFlight) {
    refreshInFlight = refreshHandler().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// ----- Client ---------------------------------------------------------------

export const supabase: SupabaseClient = createClient(url, anonKey, {
  auth: {
    // We use Firebase for auth, not Supabase auth. Disable Supabase's own
    // session persistence so it doesn't fight Firebase Auth state.
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
  global: {
    // Inject the bridged JWT on every request. supabase-js sets a default
    // `Authorization: Bearer <anonKey>` before this fetch runs; we override
    // it once we have a real bridged token. The `apikey` header (also set by
    // supabase-js) stays as the anon key — Supabase needs it to identify the
    // project, and that's orthogonal to authentication.
    fetch: async (input, init = {}) => {
      const send = () => {
        const headers = new Headers(init.headers);
        if (currentSupabaseToken) {
          headers.set('Authorization', `Bearer ${currentSupabaseToken}`);
        }
        return fetch(input, { ...init, headers });
      };
      let res = await send();
      // Reactive recovery: only when we *thought* we were authenticated.
      // (Anon requests legitimately 401 on RLS-gated objects — no retry.)
      if (res.status === 401 && currentSupabaseToken) {
        const refreshed = await tryRefreshOnce();
        if (refreshed) res = await send();
      }
      return res;
    },
  },
});

// ----- Bridge URL -----------------------------------------------------------

export const BRIDGE_URL = `${url}/functions/v1/firebase-jwt-bridge`;
export const SUPABASE_ANON_KEY = anonKey;
